'use client';

import {
  forwardRef,
  useMemo,
  type CSSProperties,
  type MouseEventHandler,
  type ReactNode,
} from 'react';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '~/components/ui/popover';
import {
  Tooltip as UiTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~/components/ui/tooltip';
import TruncatedText from '~/components/TruncatedText';
import { cn } from '~/lib/utils';

type OriginVertical = 'top' | 'center' | 'bottom' | number;
type OriginHorizontal = 'left' | 'center' | 'right' | number;

export type DropdownProps = {
  open?: boolean;
  anchorEl?: HTMLElement | null;
  onClose?: () => void;
  children?: ReactNode;
  asMenu?: boolean;
  disablePortal?: boolean;
  disableRestoreFocus?: boolean;
  /** @deprecated no-op — kept for MUI call-site compat */
  disableAutoFocus?: boolean;
  /** @deprecated no-op — kept for MUI call-site compat */
  disableEnforceFocus?: boolean;
  /** @deprecated no-op — kept for MUI call-site compat */
  disableScrollLock?: boolean;
  /** @deprecated no-op — kept for MUI call-site compat */
  marginThreshold?: number;
  hideBackdrop?: boolean;
  id?: string;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  anchorOrigin?: { vertical?: OriginVertical; horizontal?: OriginHorizontal };
  transformOrigin?: { vertical?: OriginVertical; horizontal?: OriginHorizontal };
  slotProps?: {
    paper?: { className?: string; style?: CSSProperties; sx?: unknown; [key: string]: unknown };
    root?: { style?: CSSProperties; className?: string; sx?: unknown; [key: string]: unknown };
  };
  /** Clicks/focus inside this selector do not dismiss the popover. */
  dismissIgnoreSelector?: string;
  /** Overrides origin mapping when set. */
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  alignOffset?: number;
  sideOffset?: number;
  collisionPadding?: number;
};

function eventTargetElement(target: EventTarget | null): Element | null {
  if (target instanceof Element) return target;
  if (target instanceof Node) return target.parentElement;
  return null;
}

function shouldIgnoreDismiss(
  event: { target: EventTarget | null; detail?: { originalEvent?: Event } },
  selector?: string,
) {
  if (!selector) return false;
  const candidates = [event.target, event.detail?.originalEvent?.target ?? null];
  return candidates.some((target) => eventTargetElement(target)?.closest(selector));
}

function mapSide(
  vertical?: OriginVertical,
  horizontal?: OriginHorizontal,
): 'top' | 'bottom' | 'left' | 'right' {
  if (vertical === 'center' && horizontal === 'right') return 'right';
  if (vertical === 'center' && horizontal === 'left') return 'left';
  if (vertical === 'top' || (typeof vertical === 'number' && vertical < 0)) return 'top';
  return 'bottom';
}

function mapAlign(
  side: 'top' | 'bottom' | 'left' | 'right',
  vertical?: OriginVertical,
  horizontal?: OriginHorizontal,
): 'start' | 'center' | 'end' {
  if (side === 'left' || side === 'right') {
    if (vertical === 'top') return 'start';
    if (vertical === 'bottom') return 'end';
    return 'center';
  }
  if (horizontal === 'right') return 'end';
  if (horizontal === 'center') return 'center';
  return 'start';
}

/**
 * Positioned panel facade (menus + filter popovers).
 * Keeps the historical anchorEl/open/onClose API while using Radix Popover.
 */
export default function Dropdown({
  open = false,
  anchorEl,
  onClose,
  children,
  asMenu,
  disablePortal = true,
  id,
  onClick,
  anchorOrigin,
  transformOrigin,
  slotProps,
  dismissIgnoreSelector,
  side: sideProp,
  align: alignProp,
  alignOffset,
  sideOffset = 4,
  collisionPadding = 8,
}: DropdownProps) {
  const virtualRef = useMemo(
    () => ({ current: anchorEl } as React.RefObject<HTMLElement>),
    [anchorEl],
  );

  const side = sideProp ?? mapSide(anchorOrigin?.vertical, anchorOrigin?.horizontal);
  const align = alignProp ?? mapAlign(side, anchorOrigin?.vertical, anchorOrigin?.horizontal);

  return (
    <Popover
      open={Boolean(open && anchorEl)}
      onOpenChange={(next) => {
        if (!next) onClose?.();
      }}
    >
      {anchorEl ? <PopoverAnchor virtualRef={virtualRef} /> : null}
      <PopoverContent
        id={id}
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        collisionPadding={collisionPadding}
        disablePortal={disablePortal}
        onClick={onClick}
        onMouseDown={slotProps?.paper?.onMouseDown as MouseEventHandler | undefined}
        className={cn(
          'z-101! w-auto max-w-[min(100vw-2rem,28rem)] border-border bg-popover p-0 text-popover-foreground',
          asMenu
            ? 'rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]'
            : 'rounded-xl shadow-lg',
          slotProps?.paper?.className,
        )}
        style={slotProps?.paper?.style}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => {
          if (shouldIgnoreDismiss(e, dismissIgnoreSelector)) e.preventDefault();
        }}
        onFocusOutside={(e) => {
          if (shouldIgnoreDismiss(e, dismissIgnoreSelector)) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (shouldIgnoreDismiss(e, dismissIgnoreSelector)) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (dismissIgnoreSelector && document.querySelector(dismissIgnoreSelector)) {
            e.preventDefault();
          }
        }}
      >
        {asMenu ? (
          <ul className="min-w-[10rem] w-full p-1" role="menu">
            {children}
          </ul>
        ) : (
          children
        )}
      </PopoverContent>
    </Popover>
  );
}

interface DropdownItemProps {
  children: React.ReactNode;
  icon?: React.ReactNode;
  tooltip?: React.ReactNode;
  disabled?: boolean;
  isWarning?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  shortcut?: string;
  description?: string;
}

export const DropdownItem = forwardRef<HTMLLIElement, DropdownItemProps>(
  (
    { children, icon, tooltip, disabled, isWarning, onClick, className = '', shortcut, description },
    ref,
  ) => {
    const label =
      typeof children === 'string' ? (
        <TruncatedText className={description ? 'min-w-0' : 'min-w-0 flex-1'}>
          {children}
        </TruncatedText>
      ) : (
        <span className={description ? 'min-w-0' : 'min-w-0 flex-1 truncate'}>{children}</span>
      );

    const itemContent = (
      <li ref={ref} role="menuitem" className={disabled ? 'opacity-50' : ''}>
        <button
          type="button"
          onClick={disabled ? undefined : onClick}
          className={cn(
            'flex w-full gap-2 rounded-md px-2 text-left text-[13px] font-medium tracking-[-0.01em] transition-colors',
            'outline-none focus-visible:bg-accent',
            description ? 'items-start py-2' : 'items-center py-1.5',
            isWarning
              ? 'text-destructive hover:bg-destructive/10 focus-visible:bg-destructive/10'
              : 'text-foreground hover:bg-accent',
            disabled && 'pointer-events-none cursor-not-allowed',
            className,
          )}
        >
          {icon ? (
            <span
              className={cn(
                'flex size-4 shrink-0 items-center justify-center [&_svg]:size-3.5',
                'text-muted-foreground',
                isWarning && 'text-destructive',
                description && 'mt-0.5',
              )}
            >
              {icon}
            </span>
          ) : null}
          {description ? (
            <span className="min-w-0 flex-1">
              {label}
              <span className="text-muted-foreground mt-0.5 block text-xs font-normal leading-4 tracking-normal break-words">
                {description}
              </span>
            </span>
          ) : (
            label
          )}
          {shortcut ? (
            <span className="text-muted-foreground ml-4 shrink-0 text-[11px] font-normal tracking-wide">
              {shortcut}
            </span>
          ) : null}
        </button>
      </li>
    );

    if (tooltip && disabled) {
      return (
        <TooltipProvider delayDuration={200}>
          <UiTooltip>
            <TooltipTrigger asChild>
              <span>{itemContent}</span>
            </TooltipTrigger>
            <TooltipContent side="right">{tooltip}</TooltipContent>
          </UiTooltip>
        </TooltipProvider>
      );
    }

    return itemContent;
  },
);

DropdownItem.displayName = 'DropdownItem';

export const DropdownSeparator = () => (
  <li role="separator" aria-hidden className="bg-border mx-1 my-1 h-px" />
);
